import { useState, useEffect } from 'react';
import { Linkedin, ExternalLink, Users, MessageSquare, ArrowRight, Rss } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import axios from 'axios';
import { API } from '@/App';

const PublicActuPage = () => {
  const [linkedinUrl, setLinkedinUrl] = useState('https://www.linkedin.com/in/association-sfoalc-030344244/');
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-content/linkedin`);
        if (res.data?.data) {
          if (res.data.data.url) setLinkedinUrl(res.data.data.url);
          if (res.data.data.embeds) setEmbeds(res.data.data.embeds);
        }
      } catch {}
      setLoading(false);
    };
    fetchConfig();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="public-actu-page">
      <SiteHeader />
      <SEO title="L'Actu SFOALC" description="Suivez l'actualite de la SFOALC sur LinkedIn" />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0077B5 0%, #004182 100%)' }}>
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute bottom-10 left-10 w-60 h-60 rounded-full bg-white/5" />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-semibold rounded-full mb-6">
            <Rss size={14} /> Fil d'actualite
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="actu-title">
            L'Actu SFOALC
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Retrouvez toutes nos actualites, publications et annonces sur notre profil LinkedIn
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f9fafb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      {/* LinkedIn Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8" data-testid="linkedin-profile-card">
          <div className="h-28 bg-gradient-to-r from-[#0077B5] to-[#004182] relative">
            <div className="absolute -bottom-10 left-8">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-lg flex items-center justify-center border-4 border-white">
                <img src="/SFOALC-logo-site.png" alt="SFOALC" className="w-14 h-14 object-contain" />
              </div>
            </div>
          </div>
          <div className="pt-14 pb-6 px-8">
            <h2 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Association SFOALC
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Société Francaise des Ophtalmologistes Adaptateurs de Lentilles de Contact
            </p>
            <div className="flex items-center gap-6 mb-5 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><Users size={14} /> Association professionnelle</span>
              <span className="flex items-center gap-1.5"><MessageSquare size={14} /> Ophtalmologie & Contactologie</span>
            </div>
            <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077B5] text-white rounded-full text-sm font-bold hover:bg-[#005f8d] transition-all hover:shadow-lg" data-testid="linkedin-follow-btn">
              <Linkedin size={16} /> Suivre sur LinkedIn
            </a>
          </div>
        </div>

        {/* Embedded LinkedIn posts */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Linkedin size={20} className="text-[#0077B5]" /> Dernières publications
          </h3>
          {embeds.length > 0 ? (
            <div className="space-y-4">
              {embeds.map((embed, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-gray-200">
                  {embed.title && (
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-700">{embed.title}</p>
                    </div>
                  )}
                  <iframe
                    src={embed.url}
                    height="500"
                    width="100%"
                    frameBorder="0"
                    allowFullScreen=""
                    title={embed.title || `Post LinkedIn ${i + 1}`}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="text-center py-12 text-gray-400">
                <Linkedin size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Aucune publication intégrée pour le moment</p>
                <p className="text-xs mt-1">Consultez notre profil LinkedIn pour les dernières actualités</p>
              </div>
            </div>
          )}
          <div className="mt-4 text-center">
            <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#0077B5] text-sm font-semibold hover:underline" data-testid="see-all-posts">
              Voir toutes nos publications sur LinkedIn <ExternalLink size={14} />
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
            className="group bg-white rounded-xl p-5 border border-gray-100 hover:border-[#0077B5]/30 hover:shadow-md transition-all text-center">
            <Linkedin size={28} className="text-[#0077B5] mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-900 mb-1">Profil LinkedIn</p>
            <p className="text-xs text-gray-500">Suivez notre activité</p>
            <span className="inline-flex items-center gap-1 mt-2 text-[#0077B5] text-xs font-semibold group-hover:gap-2 transition-all">
              Voir <ArrowRight size={12} />
            </span>
          </a>
          <a href="/blog" className="group bg-white rounded-xl p-5 border border-gray-100 hover:border-[#35AFE4]/30 hover:shadow-md transition-all text-center">
            <MessageSquare size={28} className="text-[#35AFE4] mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-900 mb-1">Blog SFOALC</p>
            <p className="text-xs text-gray-500">Articles et actualites</p>
            <span className="inline-flex items-center gap-1 mt-2 text-[#35AFE4] text-xs font-semibold group-hover:gap-2 transition-all">
              Lire <ArrowRight size={12} />
            </span>
          </a>
          <a href="/events" className="group bg-white rounded-xl p-5 border border-gray-100 hover:border-amber-400/30 hover:shadow-md transition-all text-center">
            <Users size={28} className="text-amber-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-900 mb-1">Agenda</p>
            <p className="text-xs text-gray-500">Congrès et événements</p>
            <span className="inline-flex items-center gap-1 mt-2 text-amber-500 text-xs font-semibold group-hover:gap-2 transition-all">
              Voir <ArrowRight size={12} />
            </span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicActuPage;
